/**
 * The catalogue's one search box: type a company name, a company number, a
 * person, a location, or any mix, and get the companies and the SIC codes
 * they file.
 *
 * The server reads the text (company-search-rules.ts), so this component sends
 * it as typed and shows back how it was read, as chips. That way an operator can
 * see at once when a part was read differently from what they meant.
 *
 * Two honesty requirements the UI has to keep:
 *   - codes are always rendered as code + description, never a bare number;
 *   - when the results came from the looser fallback search, which returns no
 *     SIC codes at all, that is stated rather than shown as "files none".
 */

import { Fragment, useRef, useState } from 'react';
import { sicApi } from '../lib/api.js';
import { useShortlist } from '../lib/shortlist-context.jsx';
import { IconCheck, IconChevronDown, IconChevronRight, IconClose, IconExternal, IconPlus, IconSearch } from './Icons.jsx';

/** Starting points shown under the box. Real, so a click returns something. */
const EXAMPLES = ['Hays', 'Firetec, Northern Ireland', '/location Ballymena', 'NI047536'];

/**
 * The tags that say which part is which. Buttons insert them so nothing has to be
 * remembered; typing them by hand works the same.
 */
const TAGS = [
  { tag: '/company', tip: 'What follows is a company name' },
  { tag: '/name', tip: "What follows is a person's name, a director for example" },
  { tag: '/location', tip: 'What follows is a place: a town, a county or a country' },
];

/** The full reading rules, for the info tip beside the box. */
const SYNTAX_HELP =
  'To say which part is which, put /company, /name (a person) or /location in front of it, in any order: ' +
  '/location Wales /company Hays /name John Smith. A tag takes everything up to the next tag. ' +
  'A plain first name and surname with no tag is searched as a person and as a company, the person\'s companies first. ' +
  'A company number goes in on its own. Without tags, commas separate the parts: Mr, Mrs, Ms, Miss or Dr in front of a name means a person, ' +
  'and England, Scotland, Wales and Northern Ireland mean a location. ' +
  'Use the first name as well as the surname: a surname alone matches thousands of companies. ' +
  'Where the box cannot tell what a part is, it asks you to add a tag rather than guess.';

/** Plain labels for each part of a reading, in the order the chips show them. */
const READING_PARTS = [
  { key: 'number', label: 'Company number' },
  { key: 'name', label: 'Company' },
  { key: 'director', label: 'Person' },
  { key: 'location', label: 'Location' },
];

/** The reading as a sentence for the results heading: “Firetec”, person X, in Y. */
function describeReading(reading) {
  const parts = [];
  if (reading.number) parts.push(`company number ${reading.number}`);
  if (reading.name) parts.push(reading.personToo ? `“${reading.name}” as a company or a person` : `“${reading.name}”`);
  if (reading.director) parts.push(`person ${reading.director}`);
  if (reading.location) parts.push(`in ${reading.location}`);
  return parts.join(', ');
}

/**
 * The person a search is about, or null. A tagged director or a plain name read as
 * a person both count. A name read only as a company does not.
 */
function personOf(reading) {
  if (reading.director) return reading.director;
  return reading.personToo ? reading.name : null;
}

/** Officer roles as words: "corporate-secretary" reads "corporate secretary". */
function formatRole(role) {
  return role ? role.replace(/-/g, ' ') : 'officer';
}

/**
 * One appointment as words. A past appointment (shown when dissolved and
 * inactive companies are included) says when it ended, so a resigned director
 * is never listed as if still in post.
 */
function describeAppointment(match) {
  const role = formatRole(match.role);
  const from = match.appointedOn ? match.appointedOn.slice(0, 4) : null;
  const to = match.resignedOn ? match.resignedOn.slice(0, 4) : null;
  if (from && to) return `${role} ${from} to ${to}`;
  if (to) return `${role} until ${to}`;
  if (from) return `${role} since ${from}`;
  return role;
}

/**
 * One line per person: the name and each of their appointments here. Grouped by
 * name because a person who is both director and secretary is one person, not
 * two entries.
 */
function formatDirectors(matches) {
  const byName = new Map();
  for (const match of matches) {
    const appointments = byName.get(match.name) ?? new Set();
    appointments.add(describeAppointment(match));
    byName.set(match.name, appointments);
  }
  return [...byName.entries()].map(([name, appointments]) => `${name} (${[...appointments].join('; ')})`);
}

function CodeChip({ entry, companyName }) {
  const shortlist = useShortlist();
  const inList = shortlist.has(entry.code);
  const busy = shortlist.isPending(entry.code);

  return (
    <div className="lookup-code">
      <span className="lookup-code-number">{entry.code}</span>
      <span className="lookup-code-description">
        {entry.description ?? (
          <span className="value-absent">
            not on the current Companies House list - a historic code this company still files
          </span>
        )}
      </span>
      <button
        type="button"
        className={`btn btn-sm ${inList ? 'btn-ghost' : ''}`}
        disabled={busy || !entry.known}
        onClick={() =>
          inList
            ? void shortlist.remove(entry.code)
            : void shortlist.add(entry.code, { source: 'company', sourceDetail: `from ${companyName}` })
        }
      >
        {busy ? <span className="spinner" /> : inList ? <IconCheck /> : <IconPlus />}
        {inList ? 'Shortlisted' : 'Shortlist'}
      </button>
    </div>
  );
}

export default function CompanyLookup() {
  const [query, setQuery] = useState('');
  // The text that produced the current results. Dissolved-toggle re-runs use
  // this, not the box, so editing the box does not silently change the results.
  const [searchedText, setSearchedText] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  // On by default, so a name search returns the longer list. Untick it for the quicker, cheaper read.
  const [readMore, setReadMore] = useState(true);
  // The codes list can be folded away; open until the operator closes it.
  const [codesOpen, setCodesOpen] = useState(true);
  // The company box in a person's results. Cleared when a new search starts.
  const [companyText, setCompanyText] = useState('');
  const [result, setResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const shortlist = useShortlist();
  // Numbers each search. A director search can take many seconds, so a newer one
  // (the dissolved toggle, say) can start before an older one answers; only the
  // newest is allowed to write its result.
  const latestSearch = useRef(0);
  const inputRef = useRef(null);

  // Adds a tag after whatever is typed, with the cursor left ready for its value.
  const addTag = (tag) => {
    setQuery((current) => `${current.trimEnd()}${current.trim() ? ' ' : ''}${tag} `);
    // After the render, so the caret lands at the end of the new text.
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    });
  };

  const search = async (text, inactive, more) => {
    const trimmed = text.trim();
    if (trimmed.length < 2) return;

    const ticket = latestSearch.current + 1;
    latestSearch.current = ticket;
    setIsSearching(true);
    setError(null);
    try {
      const found = await sicApi.companySearch(trimmed, { includeInactive: inactive, readMore: more });
      if (ticket !== latestSearch.current) return;
      setResult(found);
      setSearchedText(trimmed);
    } catch (caught) {
      if (ticket !== latestSearch.current) return;
      setError(caught.message);
      setResult(null);
    } finally {
      if (ticket === latestSearch.current) setIsSearching(false);
    }
  };

  const run = (event) => {
    event.preventDefault();
    setCompanyText('');
    void search(query, includeInactive, readMore);
  };

  const runExample = (example) => {
    setQuery(example);
    void search(example, includeInactive, readMore);
  };

  const toggleInactive = (event) => {
    const next = event.target.checked;
    setIncludeInactive(next);
    // Re-run rather than leaving stale results under a changed filter.
    if (result) void search(searchedText, next, readMore);
  };

  const toggleReadMore = (event) => {
    const next = event.target.checked;
    setReadMore(next);
    // Same re-run as the dissolved toggle: the list must match the option shown.
    if (result) void search(searchedText, includeInactive, next);
  };

  const readingParts = result ? READING_PARTS.filter((part) => result.reading[part.key]) : [];

  // The company box shows for a person's results, and turns into "show all" once a company is set.
  const person = result ? personOf(result.reading) : null;
  const narrowedToCompany = Boolean(result?.reading.name) && !result?.reading.personToo;

  // Re-runs the person's search as "this company, with this person", or as the person alone
  // when company is null. The location and both options carry over, so only the company changes.
  const searchPerson = (company) => {
    const place = result.reading.location ? ` /location ${result.reading.location}` : '';
    const companyPart = company ? `/company ${company} ` : '';
    void search(`${companyPart}/name ${person}${place}`, includeInactive, readMore);
  };

  const narrowToCompany = (event) => {
    event.preventDefault();
    const company = companyText.trim();
    if (company.length < 2) return;
    setCompanyText('');
    searchPerson(company);
  };

  // A plain first name and surname is searched as a person and as a company, the
  // person's companies first. The list says where one group ends and the other starts,
  // so a company merely named like the person is never mistaken for one they run.
  const groupTitle = (company, index) => {
    if (!result.reading.personToo) return null;
    const isTheirs = (item) => (item?.matchedDirectors?.length ?? 0) > 0;
    const theirs = isTheirs(company);
    if (index > 0 && theirs === isTheirs(result.companies[index - 1])) return null;
    return (
      <div className="lookup-group-title">
        {theirs
          ? `Companies where ${result.reading.name} is a director or officer`
          : `Companies named like “${result.reading.name}”`}
      </div>
    );
  };

  return (
    <div className="col">
      <form className="row" onSubmit={run}>
        <div className="search-field" style={{ flex: 1 }}>
          <span className="search-field-icon">
            <IconSearch />
          </span>
          <input
            ref={inputRef}
            className="input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Company, person, location or number, for example Hays, Northern Ireland"
            aria-label="Company, person, location or number"
          />
          {query ? (
            <button
              type="button"
              className="search-field-clear btn btn-ghost btn-icon btn-sm"
              onClick={() => setQuery('')}
              aria-label="Clear the search"
            >
              <IconClose />
            </button>
          ) : null}
        </div>
        <button type="submit" className="btn btn-primary" disabled={isSearching || query.trim().length < 2}>
          {isSearching ? <span className="spinner" /> : null}
          Look up
        </button>
      </form>

      {/* The tag buttons insert /company, /name or /location, so the operator says which
          part is which, in any order. The lead-in text gives way to a status while
          searching, in the same spot, so nothing below it jumps. A person search reads
          many records one at a time and is slow; saying so beats a spinner that looks stuck. */}
      {/* The tag buttons and the examples share one line, and wrap on a narrow screen. */}
      <div className="lookup-controls">
      <div className="lookup-hint">
        {isSearching ? (
          <span className="text-xs text-faint" role="status">
            Searching Companies House. A person search can take around 30 seconds.
          </span>
        ) : (
          <span className="text-xs text-faint">Mix them in any order. Say which part is which:</span>
        )}
        {TAGS.map(({ tag, tip }) => (
          <button
            key={tag}
            type="button"
            className="btn btn-sm tip tip-below"
            data-tip={tip}
            disabled={isSearching}
            onClick={() => addTag(tag)}
          >
            {tag}
          </button>
        ))}
        <span className="info-tip tip tip-below" data-tip={SYNTAX_HELP} aria-label="How the box reads what you type">
          i
        </span>
      </div>

      <div className="lookup-examples">
        <span className="text-xs text-faint">Try:</span>
        {EXAMPLES.map((example) => (
          <button
            key={example}
            type="button"
            className="btn btn-sm"
            disabled={isSearching}
            onClick={() => runExample(example)}
          >
            {example}
          </button>
        ))}
      </div>
      </div>

      {/* Both options on one line. */}
      <div className="lookup-options">
      <label className="checkbox-row tip tip-below" data-tip="Off by default. The register holds decades of dissolved companies and an unfiltered search returns mostly those, which is no use for finding who is trading in a market now.">
        <input
          type="checkbox"
          className="checkbox"
          checked={includeInactive}
          onChange={toggleInactive}
        />
        Include dissolved and inactive companies
      </label>

      <label className="checkbox-row tip tip-below" data-tip="On by default. Reads up to 30 people and 60 companies for a name search, for a longer list. Unticked, it reads 10 people and 40 companies. Each read is a Companies House call, so ticked takes longer and uses more of the shared budget. Results are kept for a day.">
        <input
          type="checkbox"
          className="checkbox"
          checked={readMore}
          onChange={toggleReadMore}
        />
        Read more people and companies (slower)
      </label>
      </div>

      {error ? (
        <div className="alert alert-error">
          <span className="alert-body">
            <span className="alert-title">Could not look that up</span>
            {error}
          </span>
        </div>
      ) : null}

      {result ? (
        <div className="card">
          <div className="card-header lookup-header">
            <span className="card-title">
              {result.hitCount.toLocaleString()} {result.hitCount === 1 ? 'company' : 'companies'}
              {describeReading(result.reading) ? ` matching ${describeReading(result.reading)}` : ''}
            </span>
            {/* Narrows a person's results to one company, or shows all their companies again. */}
            {person && narrowedToCompany ? (
              <div className="lookup-narrow">
                <button
                  type="button"
                  className="btn btn-sm btn-ghost tip tip-below"
                  data-tip={`Drops the company and lists every company ${person} is an officer of.`}
                  disabled={isSearching}
                  onClick={() => searchPerson(null)}
                >
                  Show all their companies
                </button>
              </div>
            ) : null}
            {person && !narrowedToCompany ? (
              <form className="lookup-narrow" onSubmit={narrowToCompany}>
                <div className="search-field">
                  <span className="search-field-icon">
                    <IconSearch />
                  </span>
                  <input
                    className="input"
                    type="search"
                    value={companyText}
                    onChange={(event) => setCompanyText(event.target.value)}
                    placeholder="The company you are after"
                    aria-label={`A company ${person} is an officer of`}
                    disabled={isSearching}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm tip tip-below"
                  data-tip="Finds this company among the companies they run. Type its full name: a common word matches too many companies, and only the first 20 are checked."
                  disabled={isSearching || companyText.trim().length < 2}
                >
                  Find company
                </button>
              </form>
            ) : null}
            <span className="card-header-actions">
              <span
                className="text-xs text-faint tip tip-left"
                data-tip={
                  result.cached
                    ? 'Served from the local 24-hour cache, so this cost no Companies House API calls.'
                    : 'Fetched live from Companies House and cached for 24 hours.'
                }
              >
                {result.cached ? 'cached' : 'live'}
              </span>
            </span>
          </div>

          {/* How the box read the text. A part read differently from what was meant shows here. */}
          {readingParts.length > 0 ? (
            <div className="lookup-reading" aria-label="How the search was read">
              <span className="lookup-reading-label text-xs text-faint">Read as</span>
              {readingParts.map((part) => (
                <span key={part.key} className="lookup-reading-chip">
                  <span className="lookup-reading-key">
                    {part.key === 'name' && result.reading.personToo ? 'Company or person' : part.label}
                  </span>
                  {result.reading[part.key]}
                </span>
              ))}
            </div>
          ) : null}

          {result.warnings.map((warning) => (
            <div className="card-body" key={warning}>
              <div className="alert alert-warn">
                <span className="alert-body">{warning}</span>
              </div>
            </div>
          ))}

          {/* The aggregate is the answer to "find me more like this one". */}
          {result.aggregate.length > 0 ? (
            <div className="card-body">
              {/* The header is a toggle, so a long list of codes can be folded away to reach the companies. */}
              <button
                type="button"
                className="tree-toggle codes-toggle tip"
                data-tip={codesOpen ? 'Hide the codes' : 'Show the codes'}
                onClick={() => setCodesOpen((open) => !open)}
                aria-expanded={codesOpen}
              >
                <span className="tree-toggle-chevron">
                  {codesOpen ? <IconChevronDown /> : <IconChevronRight />}
                </span>
                <span className="tree-toggle-label field-label">
                  Codes across these results
                  <span
                    className="info-tip tip"
                    data-tip="A company can file up to four SIC codes. The count shows how many of the companies found file each one - the code most of them share is usually the one worth acquiring."
                  >
                    i
                  </span>
                </span>
                <span className="pill-count">{result.aggregate.length}</span>
              </button>
              {codesOpen ? (
              <div className="lookup-codes">
                {result.aggregate.map((entry) => (
                  <div className="lookup-code" key={entry.code}>
                    <span className="lookup-code-number">{entry.code}</span>
                    <span className="lookup-code-description">
                      {entry.description ?? <span className="value-absent">unknown code</span>}
                      <span className="text-xs text-faint"> &mdash; {entry.share}</span>
                    </span>
                    <button
                      type="button"
                      className={`btn btn-sm ${shortlist.has(entry.code) ? 'btn-ghost' : 'btn-primary'}`}
                      disabled={shortlist.isPending(entry.code) || !entry.description}
                      onClick={() =>
                        shortlist.has(entry.code)
                          ? void shortlist.remove(entry.code)
                          : void shortlist.add(entry.code, {
                              source: 'company',
                              sourceDetail: `from a search for "${searchedText}"`,
                            })
                      }
                    >
                      {shortlist.has(entry.code) ? <IconCheck /> : <IconPlus />}
                      {shortlist.has(entry.code) ? 'Shortlisted' : 'Shortlist'}
                    </button>
                  </div>
                ))}
              </div>
              ) : null}
            </div>
          ) : null}

          <div>
            {result.companies.length === 0 ? (
              <div className="empty">
                <h3 className="empty-title">No companies matched that search</h3>
                <p className="empty-description">
                  Companies House matches registered names, so a trading name may not find anything.
                  A person is found by the name on the register. Check the spelling, or try a shorter
                  part.
                </p>
              </div>
            ) : (
              result.companies.map((company, index) => (
                <Fragment key={company.companyNumber}>
                {groupTitle(company, index)}
                <div className="lookup-result">
                  <div className="lookup-company">
                    <span className="lookup-company-name">{company.name}</span>
                    <a
                      className="link-external mono text-xs"
                      href={`https://find-and-update.company-information.service.gov.uk/company/${company.companyNumber}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {company.companyNumber}
                      <IconExternal />
                    </a>
                    <span
                      className={`badge ${company.status === 'active' ? 'badge-ok' : 'badge-unknown'}`}
                    >
                      <span className="badge-dot" />
                      {company.statusLabel ?? 'Unknown status'}
                    </span>
                    {company.typeLabel ? (
                      <span className="text-xs text-faint">{company.typeLabel}</span>
                    ) : null}
                    {company.incorporatedOn ? (
                      <span className="text-xs text-faint">
                        incorporated {company.incorporatedOn}
                      </span>
                    ) : null}
                  </div>

                  {company.address ? <span className="text-xs text-faint">{company.address}</span> : null}

                  {company.matchedDirectors?.length ? (
                    <div className="lookup-company-directors text-xs text-dim">
                      {formatDirectors(company.matchedDirectors).map((line) => (
                        <span key={line}>
                          <span className="text-faint">Matched: </span>
                          {line}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {company.sicCodes.length > 0 ? (
                    <div className="lookup-codes">
                      {company.sicCodes.map((entry) => (
                        <CodeChip key={entry.code} entry={entry} companyName={company.name} />
                      ))}
                    </div>
                  ) : (
                    <span className="value-absent text-sm">
                      {result.source === 'search-fallback'
                        ? 'SIC codes not available from this search'
                        : 'This company files no SIC codes'}
                    </span>
                  )}
                </div>
                </Fragment>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
